import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import {
  listClients,
  listSites,
  createClient,
  createSite,
  updateSite,
  listAssets,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, MapPin, Wrench, ChevronRight, Building2, Navigation, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openClient, setOpenClient] = useState(false);
  const [openSite, setOpenSite] = useState(false);
  const [editSite, setEditSite] = useState(null);

  const load = async () => {
    const [c, s, a] = await Promise.all([listClients(), listSites(), listAssets()]);
    setClients(c);
    setSites(s);
    setAssets(a);
    if (c.length && !selectedClient) setSelectedClient(c[0].id);
  };
  useEffect(() => {
    load();
  }, []);

  const clientSites = sites.filter((s) => s.client_id === selectedClient);
  const isAdmin = user?.role === "admin";

  return (
    <Layout
      title="Clients & Sites"
      breadcrumbs={["CRM", "Clients"]}
      actions={
        isAdmin && (
          <Dialog open={openClient} onOpenChange={setOpenClient}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-btn" className="bg-orange-500 hover:bg-orange-600 rounded-sm font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Client
              </Button>
            </DialogTrigger>
            <NewClientDialog onCreated={() => { setOpenClient(false); load(); }} />
          </Dialog>
        )
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Clients list */}
        <div className="engineered-card overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-200 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
            {clients.length} Clients
          </div>
          <div className="divide-y divide-zinc-100 max-h-[70vh] overflow-y-auto">
            {clients.map((c) => {
              const siteCount = sites.filter((s) => s.client_id === c.id).length;
              const isActive = selectedClient === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedClient(c.id)}
                  data-testid={`client-row-${c.id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-zinc-50 ${
                    isActive ? "bg-orange-50 border-l-2 border-orange-500" : ""
                  }`}
                >
                  <div className="h-10 w-10 rounded-sm bg-zinc-900 text-white flex items-center justify-center font-bold">
                    {c.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-zinc-950 truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500 truncate">
                      {c.industry} · {siteCount} site{siteCount === 1 ? "" : "s"}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-zinc-400" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Sites + assets */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          {selectedClient && (() => {
            const c = clients.find((x) => x.id === selectedClient);
            if (!c) return null;
            return (
              <div className="engineered-card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                      Client
                    </div>
                    <h2 className="font-display font-extrabold text-2xl text-zinc-950">{c.name}</h2>
                    <div className="text-sm text-zinc-600 mt-1">
                      {c.industry} · {c.contact_name} · {c.contact_email}
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      data-testid="new-site-btn"
                      onClick={() => setOpenSite(true)}
                      variant="outline"
                      className="rounded-sm"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Site
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clientSites.map((s) => {
              const siteAssets = assets.filter((a) => a.site_id === s.id);
              return (
                <div key={s.id} data-testid={`site-card-${s.id}`} className="engineered-card overflow-hidden relative">
                  <div className="h-28 bg-zinc-100 relative">
                    {s.image_url && (
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 to-transparent" />
                    {isAdmin && (
                      <button
                        data-testid={`edit-site-${s.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditSite(s);
                        }}
                        className="absolute top-2 right-2 h-7 w-7 rounded-sm bg-white/95 hover:bg-orange-500 hover:text-white text-zinc-700 flex items-center justify-center border border-zinc-200"
                        title="Edit site"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <div className="absolute bottom-2 left-3 right-3 text-white">
                      <div className="text-[10px] tracking-[0.2em] uppercase opacity-80 flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> Site
                      </div>
                      <div className="font-display font-extrabold text-lg leading-tight">{s.name}</div>
                    </div>
                  </div>
                  <div className="p-4">
                    {s.map_url ? (
                      <a
                        data-testid={`site-map-link-${s.id}`}
                        href={s.map_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-bold tracking-wider uppercase text-orange-700 hover:text-orange-800 border border-orange-300 bg-orange-50 hover:bg-orange-100 rounded-sm px-2 py-1"
                      >
                        <Navigation className="h-3 w-3" /> Open in Maps
                      </a>
                    ) : (
                      <div className="text-xs text-zinc-500">{s.city || "No map link"}</div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1 font-bold text-zinc-700">
                        <Wrench className="h-3.5 w-3.5" /> {siteAssets.length} assets
                      </span>
                      <span className="font-mono text-zinc-500">{s.country || s.city}</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {clientSites.length === 0 && (
              <div className="col-span-2 text-center py-10 text-sm text-zinc-500">
                No sites for this client.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Site create/edit dialogs */}
      <SiteDialog
        open={openSite}
        onOpenChange={setOpenSite}
        clientId={selectedClient}
        site={null}
        onSaved={() => {
          setOpenSite(false);
          load();
        }}
      />
      <SiteDialog
        open={!!editSite}
        onOpenChange={(o) => !o && setEditSite(null)}
        clientId={editSite?.client_id}
        site={editSite}
        onSaved={() => {
          setEditSite(null);
          load();
        }}
      />
    </Layout>
  );
}

function NewClientDialog({ onCreated }) {
  const [form, setForm] = useState({ name: "", industry: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setBusy(true);
    try {
      await createClient(form);
      toast.success("Client created");
      onCreated();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader><DialogTitle className="font-display text-2xl">New Client</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name" testid="client-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} full />
        <Field label="Industry" testid="client-industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} />
        <Field label="Contact name" testid="client-contact" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} />
        <Field label="Contact email" testid="client-email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} />
        <Field label="Contact phone" testid="client-phone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy} data-testid="client-submit" className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
          {busy ? "Creating…" : "Create Client"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function SiteDialog({ open, onOpenChange, clientId, site, onSaved }) {
  const editing = !!site;
  const empty = { name: "", map_url: "", city: "", country: "", image_url: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(site ? { name: site.name || "", map_url: site.map_url || "", city: site.city || "", country: site.country || "", image_url: site.image_url || "" } : empty);
    }
  }, [open, site]);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    setBusy(true);
    try {
      if (editing) {
        await updateSite(site.id, form);
        toast.success("Site updated");
      } else {
        await createSite({ client_id: clientId, ...form });
        toast.success("Site added");
      }
      onSaved();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing ? `Edit Site · ${site.name}` : "New Site"}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Site name *" testid="site-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} full />
          <div className="col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider">Google Maps link</Label>
            <Input
              data-testid="site-map-url"
              value={form.map_url}
              onChange={(e) => setForm({ ...form, map_url: e.target.value })}
              className="rounded-sm h-11"
              placeholder="https://maps.google.com/?q=..."
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              Paste any Google Maps URL (place, lat/long, or share link). Technicians tap it from the work order to open turn-by-turn navigation.
            </p>
          </div>
          <Field label="City" testid="site-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
          <Field label="Country" testid="site-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
          <div className="col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider">Image URL</Label>
            <Input
              data-testid="site-image-url"
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="rounded-sm h-11"
              placeholder="https://…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="site-submit" className="bg-orange-500 hover:bg-orange-600 rounded-sm font-bold">
            {busy ? "Saving…" : editing ? "Save" : "Create Site"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, value, onChange, testid, full }) => (
  <div className={full ? "col-span-2" : ""}>
    <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm h-11" />
  </div>
);
